import { useState } from 'react';
import { computeResults, computeYearBreakdown, fmt$, fmtPct, evaluateAttributeResults } from '../lib/samplingEngine';
import { generateReport } from '../lib/reportGenerator';
import { getTeamName } from '../lib/supabase';

const NAVY = '#0D2461', AMBER = '#F5A800', BORDER = '#c8d0e0';

export default function ResultsPanel({ project, population, sampleData, scores, profile, sampleRuns }) {
  const [selectedEstimator, setSelectedEstimator] = useState(null); // null = use recommended
  const [showRunLog, setShowRunLog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  if (!sampleData || !population) return (
    <div style={card}><div style={eyebrow}>Step 5 of 5</div><h2 style={title}>Results</h2>
      <div style={placeholder}>No sample data found.</div></div>
  );

  const drawnProjects = sampleData.drawn_projects || [];
  const large100      = sampleData.large100 || [];
  const strata        = (sampleData.strata || []).map(st => ({ ...st, projects: st.projects || [] }));

  const scoredSample = drawnProjects.map(p => ({
    ...p,
    qualifiedPct: scores[String(p._id)] != null ? parseFloat(scores[String(p._id)]) : null,
  }));

  const results = computeResults(scoredSample, strata, large100, population);

  if (!results) return (
    <div style={card}><div style={eyebrow}>Step 5 of 5</div><h2 style={title}>Results</h2>
      <div style={placeholder}>Scores are missing. Complete the scoring step first.</div></div>
  );

  // ── Attribute mode ────────────────────────────────────────────────────────
  const isAttributeMode = sampleData?.samplingMode === 'attribute';
  const attrPlan = sampleData?.attrPlan;


  async function handleGenerateReport() {
    setGenerating(true);
    try {
      const pdfMakeModule  = await import('pdfmake/build/pdfmake');
      const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
      const pdfMake  = pdfMakeModule.default  || pdfMakeModule;
      const vfs      = pdfFontsModule.default || pdfFontsModule;
      pdfMake.addVirtualFileSystem(vfs);

      let logoBase64 = null;
      try {
        const resp = await fetch('/TheLABLogo.png');
        if (resp.ok) {
          const blob = await resp.blob();
          logoBase64 = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        }
      } catch {}

      const { name: teamName } = await getTeamName(profile.team_id);

      let varResults = null;
      let varAttrResults = null;
      let yd = [];

      if (isAttributeMode && attrPlan) {
        const errCount = scoredSample.filter(p => p.qualifiedPct != null && p.qualifiedPct < 100).length;
        varAttrResults = evaluateAttributeResults(
          attrPlan.n, errCount, population.length,
          { confidenceLevel: attrPlan.confidenceLevel, tolerableRate: attrPlan.tolerableRate }
        );
      } else {
        varResults = results;
        const activeEst = selectedEstimator || results?.recommended;
        const estMap = { Mean: results?.mean, Difference: results?.difference, Ratio: results?.ratio, Regression: results?.regression };
        const activeRes = estMap[activeEst] || results?.mean;
        yd = computeYearBreakdown(population, scoredSample, activeRes?.reportedEstimate);
      }

      const docDef = generateReport({
        engagement: project,
        team: { name: teamName },
        population: { projects: population },
        sample: sampleData,
        scores,
        results: varResults,
        attrResults: varAttrResults,
        yearBreakdown: yd,
        sampleRuns: sampleRuns || [],
        preparedBy: {
          fullName: profile.full_name || '',
          date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        },
        logoBase64,
      });

      const safeName = (project.client_name || 'client').replace(/[^a-z0-9]/gi, '_');
      await pdfMake.createPdf(docDef).download(`TheLabSamplingReport_${safeName}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setPdfError('PDF generation failed: ' + (err?.message || 'Unknown error. Check console for details.'));
    } finally {
      setGenerating(false);
    }
  }

  if (isAttributeMode && attrPlan) {
    const errorsFound = scoredSample.filter(p => p.qualifiedPct != null && p.qualifiedPct < 100).length;
    const attrResults = evaluateAttributeResults(
      attrPlan.n, errorsFound, population.length,
      { confidenceLevel: attrPlan.confidenceLevel, tolerableRate: attrPlan.tolerableRate }
    );
    return (
      <div style={card}>
        <div style={eyebrow}>Step 5 of 5 — Attribute Sampling Results</div>
        <h2 style={title}>Attribute Sampling Results</h2>
        <p style={sub}>{project.client_name}{project.tax_years ? ` · Tax Years: ${project.tax_years}` : ''}</p>
        <p style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#8a96b0',marginBottom:20}}>
          Rev. Proc. 2011-42 §4.05 · {(attrPlan.confidenceLevel*100).toFixed(0)}% Confidence · Tolerable Rate: {fmtPct(attrPlan.tolerableRate)}
        </p>

        {attrResults && (
          <>
            <div style={{...primaryCard, background: attrResults.passesTest ? '#1a5c3a' : '#c03a1a', marginBottom:16}}>
              <div style={primaryLabel}>{attrResults.passesTest ? 'PASS' : 'FAIL'} — Attribute Test Result</div>
              <div style={{...primaryValue, fontSize:18, marginBottom:8}}>{attrResults.conclusion}</div>
            </div>

            <div style={resultGrid}>
              <div style={resultCard}><div style={resultLabel}>Sample Size</div>
                <div style={resultValue}>{attrResults.sampleSize}</div>
                <div style={resultNote}>Projects reviewed</div></div>
              <div style={resultCard}><div style={resultLabel}>Errors Found</div>
                <div style={{...resultValue, color: errorsFound > 0 ? '#c03a1a' : '#1a5c3a'}}>{errorsFound}</div>
                <div style={resultNote}>Projects not 100% qualifying</div></div>
              <div style={resultCard}><div style={resultLabel}>Observed Error Rate</div>
                <div style={resultValue}>{fmtPct(attrResults.observedRate)}</div>
                <div style={resultNote}>Errors / sample size</div></div>
              <div style={resultCard}><div style={resultLabel}>Upper Error Limit</div>
                <div style={{...resultValue, color: attrResults.passesTest ? '#1a5c3a' : '#c03a1a'}}>
                  {fmtPct(attrResults.upperErrorLimit)}</div>
                <div style={resultNote}>vs tolerable rate: {fmtPct(attrResults.tolerableRate)}</div></div>
            </div>

            <div style={sectionTitle}>Attribute Test Detail</div>
            <table style={table}>
              <thead><tr>{['Metric','Value','Notes'].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {[
                  ['Confidence Level', (attrPlan.confidenceLevel*100).toFixed(0)+'%', 'One-sided upper limit'],
                  ['Tolerable Error Rate', fmtPct(attrPlan.tolerableRate), 'Maximum acceptable rate'],
                  ['Expected Error Rate', fmtPct(attrPlan.expectedRate||0), 'Used for sample size calculation'],
                  ['Required Sample Size', attrPlan.n, 'Per Rev. Proc. 2011-42 §4.05'],
                  ['Actual Sample Size', attrResults.sampleSize, ''],
                  ['Errors Found', attrResults.errorsFound, 'Items not fully qualifying'],
                  ['Observed Rate', fmtPct(attrResults.observedRate), 'Errors / n'],
                  ['Upper Error Limit', fmtPct(attrResults.upperErrorLimit), 'Wilson score interval upper bound'],
                  ['Projected Population Errors', Math.round(attrResults.projectedErrors), `of ${population.length} total projects`],
                  ['Test Result', attrResults.passesTest ? 'PASS' : 'FAIL', attrResults.passesTest ? 'Error rate within tolerance' : 'Error rate exceeds tolerance'],
                ].map(([l,v,n]) => (
                  <tr key={l} style={{borderBottom:'1px solid #f0f2f8'}}>
                    <td style={{...td,fontWeight:600,color:NAVY}}>{l}</td>
                    <td style={{...td,fontWeight:700,color:'#c98a00',fontFamily:"'Roboto Mono',monospace"}}>{v}</td>
                    <td style={{...td,color:'#8a96b0'}}>{n}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{...callout, margin:'16px 0'}}>
              <strong>Interpretation:</strong> {attrResults.conclusion}
              {' '}This result is documented under Rev. Proc. 2011-42 §4.05 and will appear in the sampling plan report.
            </div>

            <div style={{marginTop:20, display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-start', flexDirection:'column'}}>
              <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                <button style={primaryBtn} onClick={() => exportAttributeCSV(scoredSample, attrResults, attrPlan, project)}>
                  ⬇ Export Attribute Workpaper (CSV)
                </button>
                <button style={{...primaryBtn, opacity: generating ? 0.5 : 1}} onClick={() => { setPdfError(null); handleGenerateReport(); }} disabled={generating}>
                  {generating ? 'Generating…' : '📄 Generate PDF Report'}
                </button>
              </div>
              {pdfError && <div style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#c03a1a',background:'#fff0ee',border:'1px solid #c03a1a',borderRadius:4,padding:'8px 12px'}}>{pdfError}</div>}
            </div>
          </>
        )}
      </div>
    );
  }

  // Determine which estimator to show
  const activeEstimator = selectedEstimator || results.recommended;
  const estimatorMap = { Mean: results.mean, Difference: results.difference, Ratio: results.ratio, Regression: results.regression };
  const activeResult = estimatorMap[activeEstimator] || results.mean;
  // otherResult available if needed for future comparison display

  const rp            = activeResult.relPrecision;
  const precisionClass= rp <= 0.10 ? 'good' : rp <= 0.15 ? 'warn' : 'bad';
  const precisionLabel= rp <= 0.10 ? '✓ Point Estimate Permitted (RP ≤ 10%)'
    : rp <= 0.15 ? '⚠ Interpolation Required (10% < RP ≤ 15%)'
    : '✗ Least Advantageous Limit Required (RP > 15%)';

  const yearBreakdown = computeYearBreakdown(population, scoredSample, activeResult.reportedEstimate);

  return (
    <div style={card}>
      <div style={eyebrow}>Step 5 of 5 — Complete</div>
      <h2 style={title}>Statistical Results</h2>
      <p style={sub}>{project.client_name}{project.tax_years ? ` · Tax Years: ${project.tax_years}` : ''}</p>

      {/* ── Small sample warning ── */}
      {results.smallSampleWarning && (
        <div style={{...callout, borderColor:'#c03a1a', background:'#fff0ee', color:'#c03a1a', marginBottom:20}}>
          ⚠ <strong>Small sample warning:</strong> Only {results.nSampled} non-certainty projects were scored.
          Results below 30 projects use the t-distribution (z={activeResult.z}) and may have reduced precision.
          This limitation will be documented in the sampling plan section of the report.
        </div>
      )}

      {/* ── Four estimator selector ── */}
      <div style={{...estimatorRow, flexWrap:'wrap'}}>
        {[
          { key: 'Mean',       result: results.mean },
          { key: 'Difference', result: results.difference },
          { key: 'Ratio',      result: results.ratio },
          { key: 'Regression', result: results.regression },
        ].map(({ key, result }) => {
          const ineligReason = results.ineligibleReasons?.[key];
          const isEligible = !!result;
          const isActive = activeEstimator === key;
          const isRec = results.recommended === key;
          return (
            <div key={key} style={{
              ...estCard(isActive, isRec),
              flex: '1 1 calc(50% - 8px)',
              minWidth: 200,
              opacity: isEligible ? 1 : 0.5,
            }}>
              <div style={estHeader}>
                <span style={estName}>{key} Estimator</span>
                {isRec && isEligible && <span style={recBadge}>✓ Recommended</span>}
                {!isEligible && <span style={{...recBadge, background:'#f0f2f8', color:'#8a96b0'}}>Not Eligible</span>}
              </div>
              {isEligible ? (
                <>
                  <div style={estValue}>{fmt$(result.reportedEstimate)}</div>
                  <div style={estMeta}>SE: {fmt$(result.se)} · RP: {fmtPct(result.relPrecision)}</div>
                  <div style={estMethod}>{result.estimateMethod}</div>
                  <button style={{...estBtn, ...(isActive ? estBtnActive : {})}}
                    onClick={() => setSelectedEstimator(key)}>
                    {isActive ? '✓ Selected' : 'Use This Estimator'}
                  </button>
                </>
              ) : (
                <div style={{fontFamily:"'Roboto Mono',monospace", fontSize:10, color:'#8a96b0', lineHeight:1.6, marginTop:8}}>
                  Ineligible: {ineligReason || 'Bias conditions not met (§4.03(3))'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ineligibility note for IRS documentation */}
      {Object.keys(results.ineligibleReasons || {}).length > 0 && (
        <div style={{...callout, marginBottom:16}}>
          <strong>Estimator eligibility per Rev. Proc. 2011-42 §4.03(3):</strong>
          {Object.entries(results.ineligibleReasons).map(([name, reason]) => (
            <div key={name} style={{marginTop:4}}>· {name}: {reason}</div>
          ))}
          This determination will be documented in the sampling plan report.
        </div>
      )}

      <div style={{fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', marginBottom:24, lineHeight:1.6}}>
        Per Rev. Proc. 2011-42 §4.03(1), the estimator with the smallest standard error is used.
        The {results.recommended} estimator has the smallest SE among eligible estimators.
        {selectedEstimator && selectedEstimator !== results.recommended && (
          <span style={{color:'#c98a00'}}> You have manually selected the {selectedEstimator} estimator — this selection will be documented in the sampling plan section of the report.</span>
        )}
      </div>

      {/* ── Primary result ── */}
      <div style={{...precisionBadge(precisionClass), marginBottom:16}}>{precisionLabel}</div>
      <div style={primaryCard}>
        <div style={primaryLabel}>Projected Qualified Amount — {activeEstimator} Estimator (Reported Estimate)</div>
        <div style={primaryValue}>{fmt$(activeResult.reportedEstimate)}</div>
        <div style={primaryNote}>{activeResult.estimateMethod}</div>
      </div>

      {/* ── Result grid ── */}
      <div style={resultGrid}>
        <div style={resultCard}><div style={resultLabel}>Point Estimate</div>
          <div style={resultValue}>{fmt$(activeResult.pointEstimate)}</div>
          <div style={resultNote}>{activeEstimator} estimator · N={results.N}</div></div>
        <div style={resultCard}><div style={resultLabel}>Relative Precision</div>
          <div style={{...resultValue, color: precisionClass==='good'?'#1a5c3a':precisionClass==='warn'?'#c98a00':'#c03a1a'}}>
            {fmtPct(rp)}</div>
          <div style={resultNote}>Target ≤10% for point estimate</div></div>
        <div style={resultCard}><div style={resultLabel}>Avg Qualification Rate ($)</div>
          <div style={resultValue}>{fmtPct(results.avgQualRate)}</div>
          <div style={resultNote}>Weighted by project cost</div></div>
        <div style={resultCard}><div style={resultLabel}>Proportion Qualifying (count)</div>
          <div style={resultValue}>{fmtPct(results.propQualifying)}</div>
          <div style={resultNote}>95% CI: {fmtPct(results.propLower)} – {fmtPct(results.propUpper)}</div></div>
      </div>

      {/* ── CI Detail ── */}
      <div style={sectionTitle}>Confidence Interval Detail — {activeEstimator} Estimator (Rev. Proc. 2011-42 §4.03)</div>
      <table style={table}>
        <thead><tr>{['Metric','Value','Notes'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {[
            ['Standard Error (SE)',       fmt$(activeResult.se),        'Of total estimated amount'],
            ['Confidence Coefficient',    activeResult.z===1.645?'1.645 (Normal)':`${activeResult.z} (t-dist)`, results.nSampled>=100?'n ≥ 100':'n < 100 — conservative t'],
            ['Precision (±)',             fmt$(activeResult.precision),  'SE × confidence coefficient'],
            ['Lower 95% One-Sided Limit', fmt$(activeResult.lowerLimit), 'Least advantageous limit'],
            ['Upper 95% One-Sided Limit', fmt$(activeResult.upperLimit), ''],
            ['Relative Precision',        fmtPct(rp),                   'Precision ÷ Point Estimate'],
            ['Sample Size (n)',           results.nSampled,              'Non-100% strata only'],
            ['Random Seed',               sampleData.seed,               'Appendix B documentation'],
          ].map(([l,v,n]) => (
            <tr key={l} style={{borderBottom:'1px solid #f0f2f8'}}>
              <td style={{...td,fontWeight:600,color:NAVY}}>{l}</td>
              <td style={{...td,fontWeight:700,color:'#c98a00',fontFamily:"'Roboto Mono',monospace"}}>{v}</td>
              <td style={{...td,color:'#8a96b0'}}>{n}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Precision callouts */}
      {rp > 0.15 && <div style={{...callout,borderColor:'#c03a1a',background:'#fff0ee',color:'#c03a1a',margin:'16px 0'}}>
        <strong>Action Required:</strong> RP exceeds 15%. The least advantageous 95% lower limit ({fmt$(activeResult.lowerLimit)}) must be used per Rev. Proc. 2011-42 Sec. 4.02(2)(c).</div>}
      {rp > 0.10 && rp <= 0.15 && <div style={{...callout,borderColor:'#c98a00',background:'#fff8e6',color:'#7a5c10',margin:'16px 0'}}>
        <strong>Interpolation Applied:</strong> RP between 10–15%. Reported estimate computed using interpolation formula.</div>}
      {rp <= 0.10 && <div style={{...callout,margin:'16px 0'}}>
        <strong>Point Estimate Permitted:</strong> RP ≤ 10%. Point estimate of {fmt$(activeResult.pointEstimate)} may be used.</div>}

      {/* ── Per-year breakdown ── */}
      {yearBreakdown.length > 0 && <>
        <div style={sectionTitle}>Per-Tax-Year QRE Breakdown</div>
        <table style={table}>
          <thead><tr>{['Tax Year','Population Wages ($)','Apportionment (%)','Estimated QREs ($)'].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {yearBreakdown.map(r => (
              <tr key={r.year} style={{borderBottom:'1px solid #f0f2f8'}}>
                <td style={{...td,fontWeight:700,color:NAVY}}>{r.year}</td>
                <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{fmt$(r.populationAmt)}</td>
                <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{fmtPct(r.apportionment)}</td>
                <td style={{...td,fontWeight:700,color:'#1a5c3a',fontFamily:"'Roboto Mono',monospace"}}>{fmt$(r.estimatedQRE)}</td>
              </tr>
            ))}
            <tr>
              <td style={{...td,fontWeight:700}}>Total</td>
              <td style={{...td,fontWeight:700,fontFamily:"'Roboto Mono',monospace"}}>{fmt$(yearBreakdown.reduce((s,r)=>s+r.populationAmt,0))}</td>
              <td style={{...td,fontWeight:700,fontFamily:"'Roboto Mono',monospace"}}>100.0%</td>
              <td style={{...td,fontWeight:800,color:'#1a5c3a',fontFamily:"'Roboto Mono',monospace"}}>{fmt$(activeResult.reportedEstimate)}</td>
            </tr>
          </tbody>
        </table>
      </>}

      {/* ── Sample workpaper ── */}
      <div style={sectionTitle}>Sample Workpaper (Appendix B)</div>
      <table style={table}>
        <thead><tr>{['#','Project','Stratum','Reported ($)','Qualified %','Qualified ($)'].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {scoredSample.map((p,i) => (
            <tr key={p._id} style={{borderBottom:'1px solid #f0f2f8',opacity:p.qualifiedPct===0?0.6:1}}>
              <td style={{...td,color:'#8a96b0'}}>{i+1}</td>
              <td style={{...td,maxWidth:260,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:600,color:NAVY}}>{p.name}</td>
              <td style={td}>{p.stratum}</td>
              <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{fmt$(p.amount)}</td>
              <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{p.qualifiedPct!=null?p.qualifiedPct+'%':'—'}</td>
              <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontWeight:600,color:'#1a5c3a'}}>{p.qualifiedPct!=null?fmt$(p.amount*p.qualifiedPct/100):'—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Sample run log ── */}
      {sampleRuns && sampleRuns.length > 0 && <>
        <div style={sectionTitle}>
          <button style={{background:'none',border:'none',cursor:'pointer',fontFamily:"'Roboto Mono',monospace",
            fontSize:10,letterSpacing:'0.1em',textTransform:'uppercase',color:'#8a96b0',padding:0}}
            onClick={() => setShowRunLog(v => !v)}>
            {showRunLog ? '▲' : '▼'} Sample Run Log ({sampleRuns.length} draw{sampleRuns.length!==1?'s':''})
          </button>
        </div>
        {showRunLog && (
          <table style={table}>
            <thead><tr>{['Run #','Drawn By','Date/Time','Seed','Sample Size','Proj. RP','Settings'].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {sampleRuns.map(run => (
                <tr key={run.id} style={{borderBottom:'1px solid #f0f2f8'}}>
                  <td style={{...td,fontWeight:700,color:NAVY}}>#{run.run_number}</td>
                  <td style={td}>{run.drawn_by ? run.drawn_by.slice(0,8)+'…' : '—'}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11}}>{new Date(run.drawn_at).toLocaleString()}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{run.seed}</td>
                  <td style={td}>{run.total_sample}</td>
                  <td style={td}>{run.projected_rp != null ? fmtPct(run.projected_rp) : '—'}</td>
                  <td style={{...td,fontSize:10,color:'#8a96b0'}}>
                    {run.settings ? `${run.settings.numStrata || 5} strata · n=${run.settings.fixedN || 5}/str` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>}

      {/* ── Export ── */}
      <div style={{marginTop:24,display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-start',flexDirection:'column'}}>
        <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
          <button style={primaryBtn} onClick={() => exportCSV(scoredSample, activeResult, results, yearBreakdown, project, activeEstimator, sampleRuns)}>
            ⬇ Export Workpaper (CSV)
          </button>
          <button style={{...primaryBtn, opacity: generating ? 0.5 : 1}} onClick={() => { setPdfError(null); handleGenerateReport(); }} disabled={generating}>
            {generating ? 'Generating…' : '📄 Generate PDF Report'}
          </button>
        </div>
        {pdfError && <div style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#c03a1a',background:'#fff0ee',border:'1px solid #c03a1a',borderRadius:4,padding:'8px 12px'}}>{pdfError}</div>}
      </div>
    </div>
  );
}

function downloadCSV(rows, filename) {
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportAttributeCSV(scoredSample, attrResults, attrPlan, project) {
  const rows = [
    [`Attribute Sampling Results — ${project.client_name}`],
    [`Tax Years: ${project.tax_years || 'N/A'}`],
    [`Method: Rev. Proc. 2011-42 §4.05`],
    [],
    ['ATTRIBUTE TEST SUMMARY'],
    ['Confidence Level', (attrPlan.confidenceLevel*100).toFixed(0)+'%'],
    ['Tolerable Error Rate', (attrPlan.tolerableRate*100).toFixed(1)+'%'],
    ['Required Sample Size', attrPlan.n],
    ['Errors Found', attrResults.errorsFound],
    ['Observed Error Rate', (attrResults.observedRate*100).toFixed(2)+'%'],
    ['Upper Error Limit', (attrResults.upperErrorLimit*100).toFixed(2)+'%'],
    ['Test Result', attrResults.passesTest ? 'PASS' : 'FAIL'],
    [],
    ['SAMPLE WORKPAPER'],
    ['#','Project','Stratum','Reported ($)','Qualified %','Result'],
    ...scoredSample.map((p,i) => [
      i+1, p.name, p.stratum, p.amount,
      p.qualifiedPct != null ? p.qualifiedPct : '',
      p.qualifiedPct === 100 ? 'No Error' : p.qualifiedPct != null ? 'Error' : '',
    ]),
  ];
  downloadCSV(rows, `attribute_sample_${project.client_name.replace(/[^a-z0-9]/gi,'_')}.csv`);
}

function exportCSV(scoredSample, activeResult, results, yearBreakdown, project, estimatorName, sampleRuns) {
  const rows = [
    [`Statistical Sampling Results — ${project.client_name}`],
    [`Tax Years: ${project.tax_years || 'N/A'}`],
    [`Estimator Used: ${estimatorName}`],
    [],
    ['STATISTICAL SUMMARY'],
    ['Reported Estimate', activeResult.reportedEstimate.toFixed(2)],
    ['Point Estimate', activeResult.pointEstimate.toFixed(2)],
    ['Relative Precision', (activeResult.relPrecision * 100).toFixed(2) + '%'],
    ['Standard Error', activeResult.se.toFixed(2)],
    ['Precision', activeResult.precision.toFixed(2)],
    ['Lower 95% Limit', activeResult.lowerLimit.toFixed(2)],
    ['Upper 95% Limit', activeResult.upperLimit.toFixed(2)],
    ['Estimate Method', activeResult.estimateMethod],
    ['Mean Estimator SE', results.mean.se.toFixed(2)],
    ['Difference Estimator SE', results.difference.se.toFixed(2)],
    ['Recommended Estimator', results.recommended],
    [],
    ...(yearBreakdown.length > 0 ? [
      ['PER-TAX-YEAR QRE BREAKDOWN'],
      ['Tax Year', 'Population Amount', 'Apportionment %', 'Estimated QRE'],
      ...yearBreakdown.map(r => [r.year, r.populationAmt.toFixed(2), (r.apportionment*100).toFixed(2)+'%', r.estimatedQRE.toFixed(2)]),
      [],
    ] : []),
    ...(sampleRuns && sampleRuns.length > 0 ? [
      ['SAMPLE RUN LOG'],
      ['Run #', 'Drawn By', 'Date/Time', 'Seed', 'Sample Size', 'Projected RP'],
      ...sampleRuns.map(r => [r.run_number, r.drawn_by ? r.drawn_by.slice(0,8) : '', new Date(r.drawn_at).toLocaleString(), r.seed, r.total_sample, r.projected_rp != null ? (r.projected_rp*100).toFixed(2)+'%' : '']),
      [],
    ] : []),
    ['SAMPLE WORKPAPER (APPENDIX B)'],
    ['#', 'Project', 'Stratum', 'Reported Amount', 'Qualified %', 'Qualified Amount'],
    ...scoredSample.map((p, i) => [i+1, p.name, p.stratum, p.amount, p.qualifiedPct??'', p.qualifiedPct!=null?(p.amount*p.qualifiedPct/100).toFixed(2):'']),
  ];
  downloadCSV(rows, `thelab_sample_${project.client_name.replace(/[^a-z0-9]/gi,'_')}.csv`);
}

// ── Style helpers ─────────────────────────────────────────────────────────────
const precisionBadge = (cls) => ({
  display:'inline-flex', alignItems:'center', padding:'5px 14px', borderRadius:20,
  fontFamily:"'Roboto Mono',monospace", fontSize:12, fontWeight:600,
  background: cls==='good'?'#e4f2eb':cls==='warn'?'#fff8e6':'#fff0ee',
  color: cls==='good'?'#1a5c3a':cls==='warn'?'#c98a00':'#c03a1a',
  border: `1px solid ${cls==='good'?'#1a5c3a':cls==='warn'?'#c98a00':'#c03a1a'}`,
});
const estCard = (active, recommended) => ({
  flex:1, border:`2px solid ${active?NAVY:recommended?AMBER:BORDER}`, borderRadius:8,
  padding:'20px 24px', background: active?'#e8ecf5':'white', transition:'all 0.15s',
});

const card        = { background:'white', border:`1.5px solid ${BORDER}`, borderRadius:8, padding:40 };
const eyebrow     = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:AMBER, marginBottom:6 };
const title       = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:24, color:NAVY, margin:'0 0 6px' };
const sub         = { fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#8a96b0', marginBottom:20 };
const placeholder = { background:'#f4f6fb', border:`1.5px dashed ${BORDER}`, borderRadius:6, padding:40, textAlign:'center', fontFamily:"'Roboto Mono',monospace", fontSize:13, color:'#8a96b0' };
const callout     = { background:'#e8ecf5', borderLeft:`3px solid ${NAVY}`, borderRadius:'0 6px 6px 0', padding:'14px 18px', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, lineHeight:1.6 };
const estimatorRow= { display:'flex', gap:16, marginBottom:16 };
const estHeader   = { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 };
const estName     = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, color:NAVY };
const recBadge    = { fontFamily:"'Roboto Mono',monospace", fontSize:10, color:'#1a5c3a', background:'#e4f2eb', padding:'2px 8px', borderRadius:10 };
const estValue    = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:24, color:NAVY, margin:'8px 0 4px' };
const estMeta     = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', marginBottom:4 };
const estMethod   = { fontFamily:"'Roboto Mono',monospace", fontSize:10, color:'#5a6a8a', marginBottom:12 };
const estBtn      = { width:'100%', padding:'8px', background:'#f4f6fb', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:12, color:NAVY, cursor:'pointer' };
const estBtnActive= { background:NAVY, color:AMBER, borderColor:NAVY };
const primaryCard = { background:NAVY, borderRadius:8, padding:'24px 28px', marginBottom:16 };
const primaryLabel= { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.6)', marginBottom:8 };
const primaryValue= { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:36, color:AMBER, marginBottom:4 };
const primaryNote = { fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'rgba(255,255,255,0.55)' };
const resultGrid  = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 };
const resultCard  = { background:'#f4f6fb', border:`1.5px solid ${BORDER}`, borderRadius:6, padding:'16px 20px' };
const resultLabel = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', color:'#8a96b0', marginBottom:6 };
const resultValue = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:22, color:NAVY, marginBottom:4 };
const resultNote  = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', lineHeight:1.5 };
const sectionTitle= { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'#8a96b0', margin:'28px 0 12px', paddingTop:20, borderTop:'1px solid #e8ecf5' };
const table       = { width:'100%', borderCollapse:'collapse', fontFamily:"'Roboto Mono',monospace", fontSize:12, marginBottom:8 };
const th          = { background:NAVY, color:AMBER, padding:'8px 12px', textAlign:'left', fontSize:10, letterSpacing:'0.06em', textTransform:'uppercase' };
const td          = { padding:'9px 12px', color:'#5a6a8a' };
const primaryBtn  = { padding:'12px 24px', background:AMBER, color:NAVY, border:'none', borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, cursor:'pointer' };
